import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import DocumentRenderer from '../components/DocumentRenderer';
import SignatureBlock from '../components/SignatureBlock';
import RichTextEditor from '../components/RichTextEditor';
import RichTextViewer from '../components/RichTextViewer';
import { createRichTextContent, isOrdinaryDocument, toRichTextContent } from '../utils/richTextDocument';
import DocumentFeedbackSection from '../components/DocumentFeedbackSection';
import DeleteDocumentButton from '../components/DeleteDocumentButton';
import api from '../services/api';
import { documentService } from '../services/documentService';
import { documentTemplateService } from '../services/documentTemplateService';
import { useAuthStore } from '../stores/authStore';
import { IQuestionnaireSubmission } from '../types/questionnaire';
import { IExecutorVerification, IDocumentTemplate } from '../types/document';
import { ISignatureUploadResponse } from '../types/form';
import AiHelperWidget from '../components/AiHelperWidget';
import { stitchColors, stitchFonts } from '../theme/tokens';

type SignatureType = 'user_signature' | 'lawyer_signature' | 'executor_signature';

const PDF_SIGNATURES: Array<{ type: SignatureType; title: string }> = [
  { type: 'user_signature', title: 'User Signature' },
  { type: 'lawyer_signature', title: 'Lawyer Signature' },
  { type: 'executor_signature', title: 'Estate Executor Signature' }
];

const buildTemplateValueMap = (submission?: IQuestionnaireSubmission | null) => {
  if (
    !submission?.responses
    || typeof submission.responses !== 'object'
    || Array.isArray(submission.responses)
  ) {
    return {};
  }

  const valueMap: Record<string, any> = { ...submission.responses };
  if (!Array.isArray(submission.fields)) {
    return valueMap;
  }

  submission.fields.forEach((field) => {
    const id = field.id?.toString?.() ?? '';
    const label = field.label?.toString?.() ?? '';
    if (!id || !label || id === label) {
      return;
    }
    if (valueMap[id] !== undefined && valueMap[label] === undefined) {
      valueMap[label] = valueMap[id];
    } else if (valueMap[label] !== undefined && valueMap[id] === undefined) {
      valueMap[id] = valueMap[label];
    }
  });

  return valueMap;
};

const renderBoldSegments = (text: string, keyPrefix: string) => {
  const parts = text.split('**');
  if (parts.length === 1) {
    return [<span key={`${keyPrefix}-plain`}>{text}</span>];
  }
  return parts.map((part, index) => {
    const isBold = index % 2 === 1;
    return (
      <span
        key={`${keyPrefix}-bold-${index}`}
        style={{ fontWeight: isBold ? 700 : 400 }}
      >
        {part}
      </span>
    );
  });
};

const renderInlineSegments = (
  text: string,
  keyPrefix: string,
  values: Record<string, any>,
  preFilledFields: string[] = []
) => {
  const preFilledSet = new Set(preFilledFields);
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.flatMap((part, index) => {
    if (!part) return [];
    if (/^\[[^\]]+\]$/.test(part)) {
      const key = part.slice(1, -1).trim();
      const value = values[key];
      const hasValue = value !== undefined && value !== null && value !== '';
      const isPreFilled = preFilledSet.has(key);

      return [
        <span
          key={`${keyPrefix}-ph-${index}`}
          style={{
            color: isPreFilled
              ? stitchColors.onTertiaryFixedVariant
              : hasValue
                ? stitchColors.onBackground
                : stitchColors.onSurfaceVariant,
            fontWeight: hasValue ? 700 : 400,
            backgroundColor: isPreFilled ? stitchColors.tertiaryFixed : undefined,
            padding: isPreFilled ? '0 4px' : undefined,
            borderRadius: isPreFilled ? 4 : undefined,
            borderBottom: !hasValue ? `1px dashed ${stitchColors.outline}` : undefined,
          }}
          title={isPreFilled ? 'Pre-filled from your questionnaire answers (read-only)' : undefined}
        >
          {hasValue ? value : part}
        </span>
      ];
    }
    return renderBoldSegments(part, `${keyPrefix}-txt-${index}`);
  });
};

const renderTemplateText = (
  templateText: string | undefined,
  values: Record<string, any>,
  preFilledFields: string[] = [],
  keyPrefix: string
) => {
  if (!templateText) return null;

  const tokens = templateText.split(/(\n)/);
  return tokens.map((tokenSegment, index) => {
    if (tokenSegment === '\n') {
      return <br key={`${keyPrefix}-br-${index}`} />;
    }

    return (
      <React.Fragment key={`${keyPrefix}-seg-${index}`}>
        {renderInlineSegments(tokenSegment, `${keyPrefix}-${index}`, values, preFilledFields)}
      </React.Fragment>
    );
  });
};

export const textToEditorContent = (text: string) => {
  const paragraphs = (text || '').split(/\r?\n\r?\n+/).map((p) => p.trim()).filter(Boolean);
  return {
    type: 'doc',
    content: paragraphs.map((para) => {
      const lines = para.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      const inlineContent: any[] = [];
      lines.forEach((line, idx) => {
        if (idx > 0) {
          inlineContent.push({ type: 'hardBreak' });
        }
        inlineContent.push({ type: 'text', text: line });
      });
      return {
        type: 'paragraph',
        content: inlineContent.length > 0 ? inlineContent : [{ type: 'text', text: '' }],
      };
    }),
  };
};

const AssignedDocument: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const documentId = id || searchParams.get('id') || searchParams.get('documentId') || '';
  const queryKeyParam = documentId || token;
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const pdfExportRef = React.useRef<HTMLDivElement>(null);

  const { user } = useAuthStore();
  const isLawyer = user?.userType === 'EL';
  const isOwner = user?.userType === 'EO';

  const [isEditing, setIsEditing] = useState(false);
  const [editorContent, setEditorContent] = useState<any>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedTemplateVersion, setSelectedTemplateVersion] = useState<number | undefined>();
  const [draftVersion, setDraftVersion] = useState<number | undefined>();
  const [unresolvedMappings, setUnresolvedMappings] = useState<string[]>([]);
  const [answerSnapshot, setAnswerSnapshot] = useState<Record<string, any>>({});
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingTemplatePreview, setIsLoadingTemplatePreview] = useState(false);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const { data: uploadedTemplatesData } = useQuery<IDocumentTemplate[]>({
    queryKey: ['uploadedTemplates'],
    queryFn: () => documentTemplateService.getUploadedTemplates(),
    enabled: Boolean(isLawyer),
  });
  const uploadedTemplates = useMemo(
    () => (uploadedTemplatesData || []).filter((t) => t.sourceImport?.status === 'succeeded' || !t.sourceImport),
    [uploadedTemplatesData]
  );


  const userQueryParam = user?.id || 'anonymous';
  const { data: submission, isLoading: loading, error: queryError } = useQuery<IQuestionnaireSubmission>({
    queryKey: ['assignedSubmission', queryKeyParam, userQueryParam],
    queryFn: async () => {
      if (documentId) {
        return documentService.getAssignedDocumentById(documentId);
      }
      if (!token) throw new Error('Missing document ID or access token.');
      return documentService.getAssignedDocumentByToken(token);
    },
    enabled: Boolean(documentId || token),
    refetchOnWindowFocus: editOpen ? false : 'always',
    retry: false
  });

  const canViewFeedback = isOwner || isLawyer;
  const ordinaryDocument = isOrdinaryDocument(submission?.sourceType, submission?.currentRevision?.content);
  const canSubmitFeedback = isOwner;

  // Derive willId from submission (if it has one linked)
  const willId = submission?.willId || submission?._id || '';
  const submissionSignatures = submission?.signatures;
  const verifiedAt = submission?.verifiedAt ?? null;

  const getSignatureUrl = useCallback((signatureType: SignatureType) => (
    submissionSignatures?.[signatureType] ?? null
  ), [submissionSignatures]);

  const signatureUrls = useMemo(() => ({
    user_signature: getSignatureUrl('user_signature'),
    lawyer_signature: getSignatureUrl('lawyer_signature'),
    executor_signature: getSignatureUrl('executor_signature')
  }), [getSignatureUrl]);

  // Upload signature mutation
  const uploadMutation = useMutation<ISignatureUploadResponse, Error, { signatureType: SignatureType; file: File }>({
    mutationFn: ({ signatureType, file }: { signatureType: SignatureType; file: File }) => {
      const activeToken = token || (submission as any)?.token;
      if (!activeToken) {
        throw new Error('Missing access token.');
      }
      return documentService.uploadSignature(activeToken, signatureType, file, submission?.contentHash || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignedSubmission', queryKeyParam] });
    },
  });

  const removeMutation = useMutation<{ message: string }, Error, SignatureType>({
    mutationFn: (signatureType: SignatureType) => {
      const activeToken = token || (submission as any)?.token;
      if (!activeToken) {
        throw new Error('Missing access token.');
      }
      return documentService.removeSignature(activeToken, signatureType, submission?.contentHash || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignedSubmission', queryKeyParam] });
    }
  });

  const handleSignatureUpload = useCallback(async (signatureType: SignatureType, file: File) => {
    await uploadMutation.mutateAsync({ signatureType, file });
  }, [uploadMutation]);

  const handleSignatureRemove = useCallback(async (signatureType: SignatureType) => {
    await removeMutation.mutateAsync(signatureType);
  }, [removeMutation]);

  const handleSignatureReplace = handleSignatureUpload;

  const isFinalized = submission?.finalized ?? false;
  const finalizedAt = submission?.finalizedAt ?? null;
  const hasSubmissionSignatures = Boolean(
    submissionSignatures
    && (
      submissionSignatures.user_signature
      || submissionSignatures.lawyer_signature
      || submissionSignatures.executor_signature
    )
  );
  const showSignatureSection = Boolean(willId || submissionSignatures || hasSubmissionSignatures);

  const error = (queryError as any)?.message || (queryError as any)?.response?.data?.error || '';
  const pageStyles = {
    minHeight: '100vh',
    px: { xs: 2, md: 4 },
    py: { xs: 3, md: 5 },
    backgroundColor: stitchColors.surfaceBright,
    color: stitchColors.onBackground,
    fontFamily: stitchFonts.body,
    position: 'relative',
    overflow: 'hidden',
    '--page-muted': stitchColors.onSurfaceVariant,
    '--card-bg': stitchColors.surfaceContainerLowest,
    '--card-border': stitchColors.outlineVariant,
    '--page-ink': stitchColors.onBackground,
    '& .MuiButton-root': {
      fontFamily: stitchFonts.body,
      textTransform: 'none' as const,
      letterSpacing: '0.02em',
    },
    '& .MuiChip-root': {
      fontFamily: stitchFonts.body,
    },
    '@keyframes floatIn': {
      '0%': { opacity: 0, transform: 'translateY(10px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
  };
  const tokenExpiryInfo = useMemo(() => {
    if (!submission?.tokenExpiresAt) return null;
    const expiresAt = new Date(submission.tokenExpiresAt);
    if (Number.isNaN(expiresAt.getTime())) return null;
    const msLeft = expiresAt.getTime() - Date.now();
    const isExpired = msLeft <= 0;
    const dateLabel = expiresAt.toLocaleDateString();
    if (isExpired) {
      return {
        text: `This access link expired on ${dateLabel}.`,
        isExpired: true
      };
    }
    // Remove the expiry countdown sentence when active
    return null;
  }, [submission?.tokenExpiresAt]);
  const permissions = submission?.permissions;
  const canDownloadPdf = Boolean(permissions?.canDownloadPdf);
  const isTokenExpired = permissions?.isExpired ?? tokenExpiryInfo?.isExpired ?? false;
  const isAssignmentRevoked = permissions?.disabledReason === 'ASSIGNMENT_REVOKED';
  const canEdit = Boolean(permissions?.canEdit) && !isFinalized && !isTokenExpired && !isAssignmentRevoked;
  const isAssignedToLawyer = Boolean(permissions?.isAssignedToLawyer);
  const isExecutor = user?.userType === 'EX' || Boolean(permissions?.allowedSignatureTypes?.includes('executor_signature'));
  const showEditButton = isLawyer || (!isExecutor && !isAssignedToLawyer && canEdit);
  const canSign = Boolean(permissions?.canSign) && !isFinalized && !isTokenExpired && !isAssignmentRevoked;
  const canVerify = Boolean(permissions?.canVerify) && !isFinalized && !isTokenExpired && !isAssignmentRevoked;
  const allowedSignatureTypes = useMemo(
    () => new Set<SignatureType>(permissions?.allowedSignatureTypes || []),
    [permissions?.allowedSignatureTypes]
  );
  const disabledAccessReason = isAssignmentRevoked
    ? 'This assignment has been revoked. The document is available for review only and can no longer be edited or signed.'
    : isTokenExpired
      ? 'This assignment link has expired. The document is available for review only and can no longer be edited or signed.'
      : !permissions?.canEdit
        ? 'You can review this document and add your permitted signature. The document itself is read-only.'
        : '';

  const handleStartEdit = async () => {
    if (!submission) return;
    setEditError(null);

    if (isLawyer) {
      const activeDocId = submission._id || documentId;
      try {
        const draftRes = await documentService.getDocumentDraft(activeDocId);
        if (draftRes.draft && draftRes.draft.status === 'active') {
          setEditorContent(draftRes.draft.editorContent);
          setSelectedTemplateId(draftRes.draft.selectedTemplateId || '');
          setSelectedTemplateVersion(draftRes.draft.selectedTemplateVersion);
          setDraftVersion(draftRes.draft.version);
          setUnresolvedMappings(draftRes.draft.unresolvedMappings || []);
          setAnswerSnapshot(draftRes.draft.answerSnapshot || {});
          setIsEditing(true);
          return;
        }
      } catch {
        // Fall back to current revision
      }

      if (ordinaryDocument) {
        setEditorContent(toRichTextContent(submission.currentRevision?.content, submission.plainText || submission.templateText).editorContent);
      } else if (submission.currentRevision?.content?.kind === 'rich_text' && submission.currentRevision?.content?.editorContent) {
        setEditorContent(submission.currentRevision.content.editorContent);
      } else {
        const baseText = submission.plainText || submission.templateText || '';
        setEditorContent(textToEditorContent(baseText));
      }
      setIsEditing(true);
    } else if (ordinaryDocument) {
      setEditorContent(toRichTextContent(submission.currentRevision?.content, submission.plainText || submission.templateText).editorContent);
      setIsEditing(true);
    } else {
      setEditOpen(true);
    }
  };

  const handleSelectTemplate = async (templateIdToSelect: string) => {
    if (hasUnsavedEdits) {
      if (!window.confirm('You have unsaved edits. Discard edits and load the selected template?')) {
        return;
      }
    }

    if (!templateIdToSelect) {
      setSelectedTemplateId('');
      setSelectedTemplateVersion(undefined);
      setUnresolvedMappings([]);
      if (ordinaryDocument) {
        setEditorContent(toRichTextContent(submission?.currentRevision?.content, submission?.plainText || submission?.templateText).editorContent);
      } else if (submission?.currentRevision?.content?.kind === 'rich_text' && submission.currentRevision?.content?.editorContent) {
        setEditorContent(submission.currentRevision.content.editorContent);
      } else {
        setEditorContent(textToEditorContent(submission?.plainText || submission?.templateText || ''));
      }
      setHasUnsavedEdits(false);
      return;
    }

    const activeDocId = submission?._id || documentId;
    const baseRevId = submission?.currentRevision?.id || submission?.currentRevision?._id;

    try {
      setIsLoadingTemplatePreview(true);
      setEditError(null);
      const preview = await documentService.getTemplatePreview(activeDocId, {
        templateId: templateIdToSelect,
        baseRevisionId: baseRevId,
      });

      setSelectedTemplateId(preview.selectedTemplateId);
      setSelectedTemplateVersion(preview.selectedTemplateVersion);
      setEditorContent(preview.editorContent);
      setUnresolvedMappings(preview.unresolvedMappings || []);
      setAnswerSnapshot(preview.answerSnapshot || {});
      setHasUnsavedEdits(true);
      setIsEditing(true);
    } catch (err: any) {
      setEditError(err?.response?.data?.message || err?.message || 'Failed to generate template preview.');
    } finally {
      setIsLoadingTemplatePreview(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!submission || !editorContent) return;
    const activeDocId = submission._id || documentId;
    const baseRevId = submission.currentRevision?.id || submission.currentRevision?._id || '';

    setIsSavingDraft(true);
    setEditError(null);
    try {
      const res = await documentService.saveDocumentDraft(activeDocId, {
        editorContent,
        selectedTemplateId: selectedTemplateId || undefined,
        selectedTemplateVersion,
        baseRevisionId: baseRevId,
        answerSnapshot,
        unresolvedMappings,
        version: draftVersion,
      });

      setDraftVersion(res.draft.version);
      setDraftSavedAt(new Date());
      setHasUnsavedEdits(false);
    } catch (err: any) {
      setEditError(err?.response?.data?.message || err?.message || 'Failed to save draft.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!submission || !editorContent) return;
    const activeDocId = submission._id || documentId;
    const baseRevId = submission.currentRevision?.id || submission.currentRevision?._id || '';

    setIsPublishing(true);
    setEditError(null);
    try {
      if (ordinaryDocument && !isLawyer) {
        await documentService.updateDocumentContent(activeDocId, {
          baseRevisionId: baseRevId || undefined,
          contentHash: submission.contentHash,
          content: createRichTextContent(editorContent),
        });
      } else {
        await documentService.publishDocument(activeDocId, {
          editorContent,
          selectedTemplateId: selectedTemplateId || undefined,
          selectedTemplateVersion,
          baseRevisionId: baseRevId,
          answerSnapshot,
          unresolvedMappings,
          draftVersion,
        });
      }

      setIsEditing(false);
      setHasUnsavedEdits(false);
      queryClient.invalidateQueries({ queryKey: ['assignedSubmission', queryKeyParam] });
    } catch (err: any) {
      setEditError(err?.response?.data?.message || err?.message || 'Failed to publish document revision.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedEdits) {
      if (!window.confirm('You have unsaved changes. Discard and return to the published document?')) {
        return;
      }
    }
    setIsEditing(false);
    setHasUnsavedEdits(false);
    setEditError(null);
  };

  const handleDownloadPdf = async () => {
    if (!pdfExportRef.current || !submission) return;

    // Refetch fresh document to verify download permission
    let freshDoc: IQuestionnaireSubmission;
    try {
      if (documentId) {
        freshDoc = await documentService.getAssignedDocumentById(documentId);
      } else if (token) {
        freshDoc = await documentService.getAssignedDocumentByToken(token);
      } else {
        return;
      }
    } catch {
      alert('Failed to verify download permission. Please reload the document.');
      return;
    }

    if (!freshDoc.permissions?.canDownloadPdf) {
      alert('Download is only permitted after all three signatures are completed and verified.');
      return;
    }

    const element = pdfExportRef.current;
    const originalSrcs: Array<{ img: HTMLImageElement; src: string }> = [];

    // Build a map of signature type → data URL through the same-origin backend proxy.
    // The image can be public and still fail canvas/html2canvas reads without bucket CORS.
    const signatureDataUrls: Partial<Record<SignatureType, string>> = {};

    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    const fetchImageAsDataUrl = async (url: string): Promise<string> => {
      const proxyRes = await api.get<Blob>(
        '/signatures/proxy-image',
        { params: { url }, responseType: 'blob' }
      );
      return await blobToDataUrl(proxyRes.data);
    };

    // Pre-convert each signature URL to a data URL
    for (const sig of PDF_SIGNATURES) {
      const url = signatureUrls[sig.type];
      if (!url) continue;
      if (url.startsWith('data:')) {
        signatureDataUrls[sig.type] = url;
        continue;
      }
      try {
        signatureDataUrls[sig.type] = await fetchImageAsDataUrl(url);
      } catch {
        // Could not convert — will remain as-is
      }
    }

    try {
      // Replace only the PDF signature images with local data URLs.
      for (const sig of PDF_SIGNATURES) {
        const dataUrl = signatureDataUrls[sig.type];
        if (!dataUrl) continue;

        const images = Array.from(
          element.querySelectorAll<HTMLImageElement>(`img[data-pdf-signature-type="${sig.type}"]`)
        );

        for (const img of images) {
          const originalSrc = img.getAttribute('src');
          if (!originalSrc || originalSrc === dataUrl) continue;

          originalSrcs.push({ img, src: originalSrc });
          img.src = dataUrl;

          // Wait for the DOM image to fully load the new data URL.
          await new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }

            const handleLoad = () => resolve();
            img.addEventListener('load', handleLoad, { once: true });
            img.addEventListener('error', handleLoad, { once: true });
          });
        }
      }

      const opt = {
        margin: 0,
        filename: `${submission.templateTitle.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
        },
        jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const },
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['.pdf-signature-footer', '.pdf-signature-card']
        }
      };

      await html2pdf().from(element).set(opt).save();
    } finally {
      // Restore original src attributes so React's DOM stays consistent.
      originalSrcs.forEach(({ img, src }) => {
        img.setAttribute('src', src);
      });
    }
  };

  const templateValueMap = useMemo(() => buildTemplateValueMap(submission), [submission]);

  const documentPlainText = useMemo(() => {
    if (submission?.plainText && typeof submission.plainText === 'string' && submission.plainText.trim()) {
      return submission.plainText.trim();
    }
    if ((submission as any)?.currentRevision?.content) {
      const { kind, bodyText, blocks, sections } = (submission as any).currentRevision.content;
      if (kind === 'will' && bodyText && typeof bodyText === 'string') return bodyText.trim();
      if (Array.isArray(blocks) && blocks.length > 0) {
        const text = blocks
          .map((b: any) => {
            if (b.type === 'table' && b.tableData) {
              const headers = (b.tableData.headers || []).join(' | ');
              const rows = (b.tableData.rows || []).map((r: any[]) => r.join(' | ')).join('\n');
              return [headers, rows].filter(Boolean).join('\n');
            }
            return b.text || '';
          })
          .filter(Boolean)
          .join('\n\n');
        if (text.trim()) return text.trim();
      }
      if (kind === 'questionnaire' && Array.isArray(sections)) {
        const text = sections
          .map((s: any) => {
            const title = s.title ? `${s.title}\n${'='.repeat(s.title.length)}` : '';
            const fields = (s.fields || []).map((f: any) => `${f.label}: ${f.displayValue || f.value || 'Not answered'}`).join('\n');
            return [title, fields].filter(Boolean).join('\n');
          })
          .filter(Boolean)
          .join('\n\n');
        if (text.trim()) return text.trim();
      }
      if (bodyText && typeof bodyText === 'string' && bodyText.trim()) return bodyText.trim();
    }
    return submission?.templateText || '';
  }, [submission]);

  const renderedDocument = useMemo(() => {
    if (!documentPlainText) return null;
    return renderTemplateText(
      documentPlainText,
      templateValueMap,
      submission?.preFilledFields || [],
      'preview'
    );
  }, [documentPlainText, templateValueMap, submission?.preFilledFields]);

  const renderedPdfDocument = useMemo(() => {
    if (!documentPlainText) return null;
    return renderTemplateText(
      documentPlainText,
      templateValueMap,
      submission?.preFilledFields || [],
      'pdf'
    );
  }, [documentPlainText, templateValueMap, submission?.preFilledFields]);

  const documentConfig = useMemo(() => {
    if (!submission) return null;
    const fields = submission.fields.map(field => ({
      ...field,
      name: field.label || field.id,
      required: field.required ?? false
    }));
    return {
      _id: submission.documentTemplateId ?? submission.formTemplateId,
      title: submission.templateTitle,
      description: submission.templateDescription || '',
      templateText: submission.templateText,
      sections: [
        {
          id: 'document_fields',
          title: 'Document Fields',
          type: 'fields',
          isRepeatable: false,
          fields
        }
      ]
    };
  }, [submission]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, any>) => {
      if (!canEdit) {
        throw new Error('You do not have permission to edit this assigned document.');
      }
      return api.patch<IQuestionnaireSubmission>(`/documents/assignments/${token}`, {
        responses: data,
        contentHash: submission?.contentHash
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignedSubmission', queryKeyParam] });
      setEditOpen(false);
    }
  });

  const handleUpdate = async (data: Record<string, any>) => {
    await updateMutation.mutateAsync(data);
  };

  const verifyMutation = useMutation<IExecutorVerification, Error, void>({
    mutationFn: () => {
      const activeToken = token || (submission as any)?.token;
      if (!activeToken) {
        throw new Error('Missing access token.');
      }
      return documentService.verifyExecutorAssignment(activeToken, submission?.contentHash || '');
    },
    onSuccess: (data) => {
      queryClient.setQueryData<IQuestionnaireSubmission>(['assignedSubmission', queryKeyParam, userQueryParam], previous => previous
        ? { ...previous, verifiedAt: data.verifiedAt, reviewReady: true } : previous);
      queryClient.invalidateQueries({ queryKey: ['assignedSubmission', queryKeyParam] });
    }
  });

  if (loading) {
    return (
      <Box sx={{ ...pageStyles, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            textAlign: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress sx={{ color: stitchColors.primary }} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Preparing your assigned document...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...pageStyles, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            maxWidth: 520,
            width: '100%',
            zIndex: 1,
          }}
        >
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            If this link is expired, please request a new assignment.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!submission) {
    return null;
  }

  // Signature counts for status display
  const sigCount = Object.values(signatureUrls).filter(Boolean).length;

  return (
    <Box sx={pageStyles}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2.1fr 1fr' },
            gap: 3,
            mb: 4
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              animation: 'floatIn 0.6s ease-out',
            }}
          >
            {submission.permissions?.canDelete && (
              <DeleteDocumentButton documentId={submission._id} title={submission.templateTitle} onDeleted={() => navigate('/assigned-documents')} />
            )}
            <Typography
              variant="overline"
              sx={{ letterSpacing: '0.2em', color: 'var(--page-muted)', fontWeight: 600 }}
            >
              Assigned Document
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: stitchFonts.headline,
                fontWeight: 600,
                mt: 1,
                color: 'var(--page-ink)',
              }}
            >
              {submission.templateTitle}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1.5, color: 'var(--page-muted)', lineHeight: 1.6 }}>
              {submission.templateDescription || 'Review the document and complete any missing details.'}
            </Typography>
            {tokenExpiryInfo && (
              <Box
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.2,
                  borderRadius: 1,
                  border: `1px solid ${stitchColors.outlineVariant}`,
                  backgroundColor: tokenExpiryInfo.isExpired ? stitchColors.errorContainer : stitchColors.secondaryContainer,
                }}
              >
                <Typography variant="body2">
                  {tokenExpiryInfo.text}
                </Typography>
              </Box>
            )}
            {disabledAccessReason && (
              <Alert
                severity={isTokenExpired ? 'warning' : 'info'}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                {disabledAccessReason}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => navigate('/assigned-documents')}
                sx={{ borderRadius: 2 }}
              >
                Back to Assigned List
              </Button>
              {isFinalized && (
                <Chip
                  icon={<LockIcon />}
                  label="Finalized"
                  size="small"
                  color="success"
                  variant="filled"
                />
              )}
              {!canEdit && (
                <Chip
                  icon={<LockIcon />}
                  label={isTokenExpired ? 'Expired' : isAssignmentRevoked ? 'Revoked' : 'Read-only'}
                  size="small"
                  color={isTokenExpired || isAssignmentRevoked ? 'warning' : 'default'}
                  variant="outlined"
                />
              )}
            </Box>
            {/* Field Status Legend */}
            {(submission.preFilledFields && submission.preFilledFields.length > 0) && (
              <Box
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.2,
                  borderRadius: 1,
                  backgroundColor: stitchColors.tertiaryFixed,
                  border: `1px solid ${stitchColors.tertiaryFixedDim}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: stitchColors.onTertiaryFixedVariant, mb: 0.5 }}>
                  Field Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 1,
                        backgroundColor: stitchColors.tertiaryFixedDim,
                        border: `1px solid ${stitchColors.onTertiaryFixedVariant}`,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: stitchColors.onTertiaryFixedVariant }}>
                      Pre-filled from your answers (read-only)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 1,
                        backgroundColor: stitchColors.surfaceContainerLowest,
                        borderBottom: `2px dashed ${stitchColors.outline}`,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'var(--page-muted)' }}>
                      Needs to be filled in
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 3.5 },
              borderRadius: 2,
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 2,
              animation: 'floatIn 0.6s ease-out 0.08s both',
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Actions
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--page-muted)' }}>
                {isFinalized
                  ? 'This will has been finalized and locked. No further edits are allowed.'
                  : isAssignedToLawyer && !isLawyer
                    ? 'Review the document and share any feedback with your lawyer below.'
                    : canEdit
                      ? 'Save your progress by submitting changes. Each update creates a new version.'
                      : 'Review the document and complete any required actions below.'}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gap: 1.2 }}>
              {canEdit && isLawyer && (
                <FormControl fullWidth size="small">
                  <InputLabel id="template-select-label">
                    {isLoadingTemplatePreview ? 'Matching Document & Populating Template with AI...' : 'Select Reusable Template'}
                  </InputLabel>
                  <Select
                    labelId="template-select-label"
                    label={isLoadingTemplatePreview ? 'Matching Document & Populating Template with AI...' : 'Select Reusable Template'}
                    value={selectedTemplateId}
                    onChange={(e) => handleSelectTemplate(e.target.value)}
                    disabled={isPublishing || isLoadingTemplatePreview}
                    sx={{ borderRadius: 2 }}
                    endAdornment={isLoadingTemplatePreview ? <CircularProgress size={18} sx={{ mr: 2 }} /> : undefined}
                  >
                    <MenuItem value="">
                      <em>Current Document Content</em>
                    </MenuItem>
                    {uploadedTemplates.map((tpl) => (
                      <MenuItem key={tpl._id} value={tpl._id}>
                        {tpl.title} (v{tpl.version || 1})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {showEditButton && (
                <Button
                  variant="contained"
                  startIcon={isFinalized ? <LockIcon /> : <EditIcon />}
                  onClick={handleStartEdit}
                  disabled={!canEdit}
                  sx={{ borderRadius: 2 }}
                >
                  {canEdit ? (ordinaryDocument ? 'Edit Document' : 'Edit Answers') : 'Read-only'}
                </Button>
              )}
              <Tooltip title={!canDownloadPdf ? 'Download available once all three signatures are completed' : ''}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPdf}
                    disabled={!canDownloadPdf}
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    Download PDF
                  </Button>
                </span>
              </Tooltip>
              {canVerify && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => verifyMutation.mutate()}
                  disabled={verifyMutation.isPending || !!verifiedAt}
                  data-testid="verify-assignment"
                  sx={{ borderRadius: 2 }}
                >
                  {verifiedAt
                    ? `Acknowledged on ${new Date(verifiedAt).toLocaleDateString()}`
                    : verifyMutation.isPending
                      ? 'Acknowledging...'
                      : 'Acknowledge Draft Document'}
                </Button>
              )}
              {verifyMutation.error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {verifyMutation.error?.message || 'Failed to acknowledge this document.'}
                </Alert>
              )}
            </Box>
          </Paper>
        </Box>

        {editError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {editError}
          </Alert>
        )}

        {isEditing ? (
          <Box sx={{ animation: 'floatIn 0.3s ease-out' }}>
            {unresolvedMappings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                {unresolvedMappings.length} placeholder(s) could not be matched automatically and were set to '_____': {unresolvedMappings.join(', ')}. Please review and edit below.
              </Alert>
            )}
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Inline Document Editor
                {selectedTemplateId && ` (Template Preview)`}
              </Typography>
              {draftSavedAt && (
                <Typography variant="caption" sx={{ color: 'var(--page-muted)' }}>
                  Draft saved at {draftSavedAt.toLocaleTimeString()}
                </Typography>
              )}
            </Box>

            <RichTextEditor
              content={editorContent}
              preserveLayout={ordinaryDocument}
              onChange={(newContent) => {
                setEditorContent(newContent);
                setHasUnsavedEdits(true);
              }}
            />

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={isPublishing || isSavingDraft}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              {isLawyer && <Button
                variant="outlined"
                color="secondary"
                onClick={handleSaveDraft}
                disabled={isPublishing || isSavingDraft}
                sx={{ borderRadius: 2 }}
              >
                {isSavingDraft ? 'Saving Draft...' : 'Save Draft'}
              </Button>}
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublish}
                disabled={isPublishing || isSavingDraft}
                sx={{ borderRadius: 2 }}
              >
                {isPublishing ? 'Publishing...' : ordinaryDocument && !isLawyer ? 'Save as Revision' : 'Approve & Share for Review'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ animation: 'floatIn 0.6s ease-out 0.16s both' }}>
            <Box
              sx={{
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                backgroundColor: stitchColors.surfaceContainerLowest,
                border: `1px solid ${stitchColors.outlineVariant}`,
                fontFamily: stitchFonts.headline,
                fontSize: '1rem',
                lineHeight: 1.8,
                color: stitchColors.onBackground,
              }}
            >
              {ordinaryDocument ? (
                <RichTextEditor
                  content={toRichTextContent(submission.currentRevision?.content, submission.plainText || submission.templateText).editorContent}
                  editable={false}
                  preserveLayout
                />
              ) : submission.currentRevision?.content?.kind === 'rich_text' && submission.currentRevision?.content?.editorContent ? (
                <RichTextViewer content={submission.currentRevision.content.editorContent} />
              ) : renderedDocument ? (
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{renderedDocument}</Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No document content available.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ════════════════════════════════════════════════
            SIGNATURE COLLECTION SECTION
           ════════════════════════════════════════════════ */}
        {showSignatureSection && (
          <Box sx={{ mt: 5, animation: 'floatIn 0.6s ease-out 0.24s both' }}>
            <Divider sx={{ mb: 4, borderColor: stitchColors.outlineVariant }} />

            {/* Section Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="overline" sx={{ letterSpacing: '0.2em', color: 'var(--page-muted)', fontWeight: 600 }}>
                Document Signatures
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: stitchFonts.headline,
                  fontWeight: 600,
                  mt: 0.5,
                  color: 'var(--page-ink)',
                }}
              >
                Multi-Party Signature Collection
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 1, color: 'var(--page-muted)', maxWidth: 600, mx: 'auto' }}
              >
                Signatures are collected sequentially: <strong>1. Estate Owner</strong> &rarr; <strong>2. Estate Lawyer</strong> &rarr; <strong>3. Estate Executor</strong>.
                All three signatures are required to finalize this will. Once all signatures are collected, the document will be permanently locked.
              </Typography>

              {/* Progress Indicator */}
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <Chip
                  icon={isFinalized ? <CheckCircleIcon /> : undefined}
                  label={isFinalized ? 'All signatures collected — Finalized' : `${sigCount} of 3 signatures collected`}
                  color={isFinalized ? 'success' : sigCount > 0 ? 'warning' : 'default'}
                  variant={isFinalized ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            {/* Finalized Banner */}
            {isFinalized && (
              <Alert
                severity="success"
                variant="filled"
                icon={<LockIcon />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { alignItems: 'center' }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  This will has been finalized
                </Typography>
                <Typography variant="body2">
                  All three signatures have been collected. The document is now permanently locked
                  and cannot be modified.
                  {finalizedAt && (
                    <> Finalized on {new Date(finalizedAt).toLocaleDateString()}.</>
                  )}
                </Typography>
              </Alert>
            )}

            {/* Three Signature Blocks */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 3
                }}
              >
                {PDF_SIGNATURES.map((signature) => {
                  const canManageSignature = canSign && allowedSignatureTypes.has(signature.type);

                  // Sequence dependency checks (applies when uploading a new signature)
                  let sequenceLocked = false;
                  let sequenceReason: string | undefined;
                  const isUploaded = Boolean(signatureUrls[signature.type]);

                  if ((signature.type === 'user_signature' && (signatureUrls.lawyer_signature || signatureUrls.executor_signature))
                    || (signature.type === 'lawyer_signature' && signatureUrls.executor_signature)) {
                    sequenceLocked = true;
                    sequenceReason = 'Remove downstream signatures before changing this signature.';
                  } else if (!isUploaded) {
                    if (signature.type === 'lawyer_signature') {
                      if (!signatureUrls.user_signature) {
                        sequenceLocked = true;
                        sequenceReason = 'Waiting for Estate Owner to sign first (Step 1).';
                      }
                    } else if (signature.type === 'executor_signature') {
                      if (!signatureUrls.user_signature || !signatureUrls.lawyer_signature) {
                        sequenceLocked = true;
                        sequenceReason = 'Waiting for Estate Owner and Lawyer signatures (Steps 1 & 2).';
                      }
                    }
                  }

                  const signatureDisabledReason = isFinalized
                    ? 'This will has been finalized.'
                    : isTokenExpired
                      ? 'This assignment link has expired.'
                      : isAssignmentRevoked
                        ? 'This assignment has been revoked.'
                        : !canManageSignature
                          ? 'This signature can only be managed by the assigned role.'
                          : sequenceLocked
                            ? sequenceReason
                            : undefined;

                  const isBlockDisabled = !canManageSignature || sequenceLocked;

                  return (
                    <SignatureBlock
                      key={signature.type}
                      signatureType={signature.type}
                      signatureUrl={signatureUrls[signature.type]}
                      finalized={isFinalized}
                      disabled={isBlockDisabled}
                      disabledReason={signatureDisabledReason}
                      onUpload={handleSignatureUpload}
                      onRemove={handleSignatureRemove}
                      onReplace={handleSignatureReplace}
                    />
                  );
                })}
              </Box>
          </Box>
        )}

        {submission && (
          <DocumentFeedbackSection
            documentId={submission._id || documentId}
            currentRevision={
              submission.currentRevision
                ? {
                    id: (submission.currentRevision as any).id || (submission.currentRevision as any)._id || '',
                    number: (submission.currentRevision as any).revisionNumber ?? (submission.currentRevision as any).number ?? 1,
                    contentHash: submission.currentRevision.contentHash || submission.contentHash || '',
                  }
                : null
            }
            canSubmitFeedback={canSubmitFeedback}
            canViewFeedback={canViewFeedback}
            isFinalized={isFinalized}
          />
        )}
      </Box>

      <AiHelperWidget documentTemplateId={submission.documentTemplateId ?? submission.formTemplateId} assignmentToken={token} />
      <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '210mm',
            pointerEvents: 'none',
            zIndex: -1
          }}
      >
        <div ref={pdfExportRef} style={{ width: '210mm', backgroundColor: '#ffffff', color: '#201911' }}>
          <div
            style={{
              width: '210mm',
              minHeight: 'calc(297mm - 14mm - 12mm)',
              padding: '14mm 16mm 12mm',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              fontFamily: '"Garamond", "Georgia", serif',
              fontSize: '11pt',
              lineHeight: 1.8,
              color: '#201911'
            }}
          >
            <div
              style={{
                flex: '1 0 auto',
                whiteSpace: ordinaryDocument || submission?.currentRevision?.content?.kind === 'rich_text' ? 'normal' : 'pre-wrap',
              }}
            >
              {ordinaryDocument ? (
                <RichTextEditor
                  content={toRichTextContent(submission.currentRevision?.content, submission.plainText || submission.templateText).editorContent}
                  editable={false}
                  preserveLayout
                />
              ) : submission?.currentRevision?.content?.kind === 'rich_text' && submission?.currentRevision?.content?.editorContent ? (
                <RichTextViewer content={submission.currentRevision.content.editorContent} />
              ) : (
                renderedPdfDocument
              )}
            </div>

            {showSignatureSection && (
              <div
                className="pdf-signature-footer"
                style={{
                  marginTop: 'auto',
                  paddingTop: '10mm',
                  borderTop: '1px solid #d6d6d6',
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid'
                }}
              >
                <div
                  style={{
                    marginBottom: '4mm',
                    fontSize: '10pt',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#5c5347'
                  }}
                >
                  Signatures
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '5mm'
                  }}
                >
                  {PDF_SIGNATURES.map((signature) => {
                    const signatureUrl = signatureUrls[signature.type];

                    return (
                      <div
                        key={`pdf-signature-${signature.type}`}
                        className="pdf-signature-card"
                        style={{
                          border: '1px solid #dfdfdf',
                          padding: '5mm 4mm 4mm',
                          minHeight: '34mm',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          breakInside: 'avoid',
                          pageBreakInside: 'avoid',
                          backgroundColor: '#ffffff'
                        }}
                      >
                        <div
                          style={{
                            marginBottom: '4mm',
                            fontSize: '8.5pt',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#6b6357',
                            letterSpacing: '0.04em'
                          }}
                        >
                          {signature.title}
                        </div>
                        {signatureUrl ? (
                          <div>
                            <img
                              data-pdf-signature-type={signature.type}
                              src={signatureUrl}
                              alt={signature.title}
                              crossOrigin="anonymous"
                              style={{
                                display: 'block',
                                maxWidth: '100%',
                                maxHeight: '18mm',
                                objectFit: 'contain',
                                marginBottom: '4mm'
                              }}
                            />
                            <div style={{ borderBottom: '1px solid #b9b9b9' }} />
                          </div>
                        ) : (
                          <div
                            style={{
                              borderBottom: '1px solid #b9b9b9',
                              height: '18mm'
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, border: `1px solid ${stitchColors.outlineVariant}` },
        }}
      >
        <DialogTitle sx={{ fontFamily: stitchFonts.headline, fontWeight: 600 }}>
          {canEdit ? 'Edit Responses' : 'Review Responses'}
        </DialogTitle>
        <DialogContent dividers>
          {updateMutation.error && <Alert severity="error" sx={{ mb: 2 }}>
            {(updateMutation.error as any)?.response?.data?.error || updateMutation.error.message}
          </Alert>}
          {(submission.reviewReady || hasSubmissionSignatures) && <Alert severity="info" sx={{ mb: 2 }}>
            Saving changes will create a new document revision and require new signatures.
          </Alert>}
          {documentConfig && (
            <DocumentRenderer
              documentConfig={documentConfig as any}
              initialValues={templateValueMap}
              readOnlyFields={canEdit ? [] : submission.preFilledFields || []}
              onSubmit={handleUpdate}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignedDocument;
